import { Socket } from 'phoenix';
import { endpointByEnv } from './constants';
import { retryDelayWithJitter } from '../utils';
import { isUnauthorizedError } from '../axios';
import { getConfig, isWidgetConfig } from '../config';
const isClosure = (value) => typeof value === 'function';
/**
 * Wraps value in closure or returns closure
 * taken from https://github.com/phoenixframework/phoenix/blob/963cd078c77fbe03ec35edc75c656bd07029d04c/assets/js/phoenix/utils.js
 */
const closure = (value) => (isClosure(value) ? value : () => value);
/**
 * Represents a custom Socket class that enhances the functionality of the original Socket class.
 * The enhancements include handling authentication, joining and leaving channels, and refreshing access tokens.
 */
export class ConverseSocket extends Socket {
    getToken;
    constructor(getToken, params) {
        const config = getConfig();
        const platformParams = {
            language: window.navigator.language,
            platform: isWidgetConfig(config) && config.platform ? config.platform : 'web',
            client: config.client.map(pair => pair.filter(Boolean).join('/')).join('; '),
        };
        super(`${endpointByEnv[config.env]}/socket`, {
            reconnectAfterMs: retryDelayWithJitter,
            logger: config.env === 'local' || config.env === 'development'
                ? (...args) => {
                    // eslint-disable-next-line no-console
                    console.info.call(console, 'ConverseSocket:', ...args);
                }
                : undefined,
            params: {
                ...params,
                ...platformParams,
            },
            debug: config.env === 'local' || config.env === 'development',
        });
        this.getToken = getToken;
        this.onOpen(() => {
            if (this.authChannel?.state === 'joined') {
                return;
            }
            this.authChannel = this.channel(`${config.sdkType}:auth`);
            this.authChannel.join();
        });
        this.onClose(() => {
            this.authChannel?.leave();
            this.authChannel = undefined;
        });
        // subscribe for replies to all "service" events
        // these include replies to "heartbeat" pings that happen every 30sec or so
        //
        // assumption here is that it's better to check for new access token
        // and send it in "heartbeat" ping response because this guarantees that socket is still alive
        this.channel('phoenix').on('phx_reply', () => {
            void this.maybeRefreshAccessToken();
        });
    }
    authChannel;
    accessToken;
    didCatchUnauthorizedError = false;
    async maybeGetToken() {
        if (this.didCatchUnauthorizedError) {
            return;
        }
        return this.getToken().catch(err => {
            if (isUnauthorizedError(err)) {
                this.didCatchUnauthorizedError = true;
            }
        });
    }
    async maybeRefreshAccessToken() {
        const refreshedToken = await this.maybeGetToken();
        if (refreshedToken &&
            refreshedToken !== this.accessToken &&
            this.authChannel?.state === 'joined') {
            this.accessToken = refreshedToken;
            this.authChannel.push('set_access_token', { access_token: this.accessToken });
        }
    }
    get params() {
        // @ts-expect-error phoenix class definition is missing "params" private property
        return super.params;
    }
    set params(value) {
        // @ts-expect-error phoenix class definition is missing "params" private property
        super.params = value;
    }
    /**
     * This method initiates new WebSocket connections and is invoked by phoenix internally on:
     *   - initial connection
     *   - any consecutive connections (if e.g. old connection was closed with or without errors)
     *
     * We re-define it here to add ability to fetch converse token asynchronously
     * and add it to params that phoenix will then append to socket endpoint URL
     */
    async connect() {
        const accessToken = await this.maybeGetToken();
        if (accessToken) {
            this.accessToken = accessToken;
            this.params = closure({ ...this.params(), token: this.accessToken });
            super.connect();
        }
    }
    /**
     * Wraps phoenix's channel() method to:
     *   - handle "forbidden" and "unauthorized" errors
     *   - handle "ratelimited" error
     *
     * Rate limit handling flow:
     *  - Register onMessage() handler to be able to access join reply payload.
     *    Important: onMessage() is called before any other handlers (including default phoenix handlers)
     *  - If join reply payload contains "ratelimited" error, set retryJoinAfterMs to "retry_after" value from join reply payload
     *  - Redefine timerCalc to use retryJoinAfterMs if it's set or fallback to original timerCalc method implementation
     *
     * Forbidden and unauthorized errors handling flow:
     *  - Listen to "error" event on joinPush
     *  - If error reason is "forbidden" or "unauthorized", leave channel
     *    Important: this is needed because phoenix will keep retrying forever otherwise
     *
     * Note: this method will throw an error if it detects that phoenix's channel() implementation has changed
     */
    channel(topic, chanParams) {
        const channel = super.channel(topic, chanParams);
        // since we're modifying internals, check that they actually exist
        if (!channel.rejoinTimer ||
            !channel.rejoinTimer.timerCalc ||
            !channel.joinPush ||
            !channel.joinRef) {
            throw new Error('Socket.channel() implementation has changed. Please update this code.');
        }
        // to be used to store retry_after value from join reply payload
        // note: no need to reset this because channel.join() is called only once
        let retryJoinAfterMs;
        // note: onMessage() is called before any other handlers (including default phoenix handlers)
        channel.onMessage = (event, payload, ref) => {
            // check that reply ref equals join ref to make sure that we're handling replies to join requests
            if (ref === channel.joinRef() && event === 'phx_reply' && payload.status === 'error') {
                switch (payload.response.reason) {
                    case 'ratelimited':
                        // set retryJoinAfterMs to "retry_after" value from join reply payload
                        retryJoinAfterMs = Number(payload.response.retry_after) * 1_000;
                        break;
                    default:
                }
            }
            return payload;
        };
        // store original timerCalc method to be able to use it for anything other than "ratelimited" error
        const originalTimerCalc = channel.rejoinTimer.timerCalc;
        // redefine timerCalc to use retryJoinAfterMs if it's set
        channel.rejoinTimer.timerCalc = (...args) => {
            if (retryJoinAfterMs) {
                return retryJoinAfterMs;
            }
            // call original timerCalc method for anything other than "ratelimited" error
            return originalTimerCalc(...args);
        };
        // leave channel if it's forbidden or unauthorized because there's no point in retrying
        // and phoenix will keep retrying forever
        channel.joinPush.receive('error', (error) => {
            if (error.reason === 'forbidden' || error.reason === 'unauthorized') {
                channel.leave();
            }
        });
        return channel;
    }
}
//# sourceMappingURL=ConverseSocket.js.map